import { useState, type ReactNode } from 'react'
import { QueryClientProvider, type QueryClient } from '@tanstack/react-query'
import type { AuthContextType } from './auth/jwt-context'

type SessionQueryClientProviderProps = {
  /** Crée le client de la session : appelé une seule fois par montage. */
  createClient: () => QueryClient
  children: ReactNode
}

/**
 * Le client React Query d'une session utilisateur. À poser avec `key={sessionKeyOf(auth)}` : quand
 * l'utilisateur change (connexion, déconnexion, usurpation), React remonte le sous-arbre avec un
 * client neuf, et rien du compte précédent ne s'affiche dans la session suivante.
 *
 * Jusqu'au 16/10/2026, App recréait son client à chaque rendu, donc à chaque navigation : un écran
 * resté monté (paramètres d'URL) lisait un ancien client pendant que ses invalidations visaient le
 * nouveau, et il ne se rafraîchissait plus après une écriture.
 */
export const SessionQueryClientProvider = ({
  createClient,
  children,
}: SessionQueryClientProviderProps) => {
  const [client] = useState(createClient)
  return <QueryClientProvider client={client}>{children}</QueryClientProvider>
}

/** La session vue par le cache : l'utilisateur connecté, ou personne. */
export const sessionKeyOf = (
  auth: Pick<AuthContextType, 'isAuthenticated' | 'user'>
): string => (auth.isAuthenticated && auth.user?.uid) || 'anonymous'
